import React from 'react';

import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

import { VennueAccessRequestItem } from '../../../graphql/API';
import LoadingButton from '../../Button/LoadingButton';
import BaseDialog, { BaseDialogProps } from '../Base';

export const accessRequestDeclineConfirmationStorageKey = 'dont-show-decline-confirmation-popup';

type Props = {
  accessRequest: VennueAccessRequestItem | null;
  onDecline: (accessRequest: VennueAccessRequestItem | null) => void;
  venueID?: string;
} & BaseDialogProps;

export const AccessRequestDeclineConfirmationDialog: React.FC<Props> = ({
  accessRequest,
  onDecline,
  venueID,
  ...restProps
}) => {
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  const handleDontShowAgainChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDontShowAgain(e.target.checked);
    },
    [],
  );

  const handleDecline = React.useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(accessRequestDeclineConfirmationStorageKey + venueID, JSON.stringify(true));
    }
    onDecline(accessRequest);
    restProps.onClose?.({}, 'backdropClick');
  }, [dontShowAgain, onDecline, accessRequest, restProps, venueID]);

  return (
    <BaseDialog
      width='465px'
      minWidth='465px'
      height='auto'
      minHeight='auto'
      titleComponent={
        <Typography fontSize={'20px'} fontFamily={'Poppins'} color={'#000'} fontWeight={600}>
          Are you sure you want to decline this request?
        </Typography>
      }
      {...restProps}
      style={{borderRadius: '50px'}}
    >
      <Box display={'flex'} flexDirection={'column'} px={2} py={2} gap={3}>
        <Typography fontSize={'15px'} fontFamily={'Poppins'} color={'#111'}>
          If you decline this request, the user will no longer be able to apply for this offer again.
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={handleDontShowAgainChange}
              sx={{ color: '#111' }}
            />
          }
          label="Don't show this again"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '15px',
              fontFamily: 'Poppins',
            },
          }}
        />

        <Box display={'flex'} alignItems={'center'} gap={1.2} mt={1}>
          <LoadingButton
            fullWidth
            variant='outlined'
            color='primary'
            sx={{ border: '1px solid rgba(0, 0, 0, 0.10)', p: 0.8 }}
            onClick={() => {
              restProps.onClose?.({}, 'backdropClick');
            }}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            fullWidth
            variant='contained'
            color='primary'
            sx={{ p: 0.8 }}
            onClick={handleDecline}
          >
            Decline request
          </LoadingButton>
        </Box>
      </Box>
    </BaseDialog>
  );
};
